import { useRef, useState, useSyncExternalStore, type ComponentType } from "react";
import { motionOn, prefersReducedMotion, useInView } from "../../shared/motion";
import { CreateScreen } from "./screens/CreateScreen";
import { DetailScreen } from "./screens/DetailScreen";
import { HomeScreen } from "./screens/HomeScreen";

interface ScreenProps {
  play: boolean;
  still: boolean;
}

/** The four live screens, one per step (handoff how-screens.html SCREENS). */
const SCREENS: readonly ComponentType<ScreenProps>[] = [
  (p) => <HomeScreen {...p} />,
  (p) => <CreateScreen mode="fin" {...p} />,
  (p) => <CreateScreen mode="sign" {...p} />,
  (p) => <DetailScreen {...p} />,
];

const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";
const noopSubscribe = () => () => {};
const subscribeReduce = (cb: () => void) => {
  const mq = window.matchMedia(REDUCE_QUERY);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
};
const serverOff = () => false;

/** Mount keys per screen: 0 = not mounted yet; a new value remounts the screen so its script replays. */
interface Mounts {
  step: number;
  play: boolean;
  keys: readonly number[];
  seq: number;
}

// The «Qanday ishlaydi» phone: the handoff's HowApp ported to React (no iframe). Only the active
// screen plays, and only while the phone is in view with motion on. Each step change, and each return
// into view, remounts the active screen with a new key, so its scripted demo starts over (handoff
// `keys.current[i] = ++seq`). Prerender and the first client render: Home only, at rest — identical,
// so hydration is safe. Reduced motion: screens render their final state, no timers. Decorative:
// the step text lives in HowItWorks, so the whole phone is aria-hidden.
export function HowPhone({ step }: { step: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { threshold: 0.3 });
  const motion = useSyncExternalStore(noopSubscribe, motionOn, serverOff);
  const still = useSyncExternalStore(subscribeReduce, prefersReducedMotion, serverOff);
  const play = inView && motion && !still;

  // Adjusted during render when step/play change (React's "store info from previous renders"
  // pattern) — not in an effect, so the remount lands in the same commit.
  const [m, setM] = useState<Mounts>({ step, play: false, keys: [1, 0, 0, 0], seq: 1 });
  if (m.step !== step || m.play !== play) {
    const remount = step !== m.step || (play && !m.play);
    const seq = remount ? m.seq + 1 : m.seq;
    setM({ step, play, seq, keys: remount ? m.keys.map((k, i) => (i === step ? seq : k)) : m.keys });
  }

  return (
    <div className="how-live" ref={ref} aria-hidden="true">
      {SCREENS.map((Screen, i) => (
        <div key={i} className={"hk-screen" + (i === step ? " on" : "")}>
          {m.keys[i] > 0 && <Screen key={m.keys[i]} play={play && i === step} still={still} />}
        </div>
      ))}
    </div>
  );
}
